/**
 * Tower.tsx
 *
 * Single-deal Tower view with side-by-side Helix.
 *
 * LEFT  — Tower: where the deal is in the approval ladder (CP1 → CP10).
 * RIGHT — Helix: health of that same deal — qualifying signals, "I think → I know".
 *
 * Platform light theme (W66.N — 2026-06-01, inner rails W66.Q):
 *   BG            #F9FAFB   page background
 *   CARD          #FFFFFF   card background
 *   BORDER        #E5E7EB   card borders
 *   TEXT_PRIMARY  #111827   primary text
 *   TEXT_SECONDARY #374151  secondary text
 *   TEXT_MUTED    #6B7280   muted / label text
 *   MI6_RED       #DC2626   LOCKED CANON — active / passed
 *   GREEN         #16A34A   LOCKED CANON
 *   ORANGE        #EA580C   LOCKED CANON
 *   YELLOW        #EAB308   LOCKED CANON
 *   PROGRESS_TRACK #F3F4F6  progress bar track
 *
 * Display: DM Sans · Body: Inter
 * No blue, navy, slate, sky, indigo, cyan.
 */

import { useState, useMemo, useEffect } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { ColorLegendFooter } from "@/components/ColorLegendFooter";
import { getSession, getCurrentTenant, supabase } from "@/lib/session";
import OperationalHelix from "@/components/helix/OperationalHelix";
import { getOrbCpFromRows } from "@shared/scoring/orb-position";

/* ─────────────────────────────────────────────────────────────────────────── */
/* Color tokens — platform light theme (W66.N)                                 */
/* ─────────────────────────────────────────────────────────────────────────── */

const BG             = "#F9FAFB";
const CARD           = "#FFFFFF";
const BORDER         = "#E5E7EB";
const TEXT_PRIMARY   = "#111827";
const TEXT_SECONDARY = "#374151";
const TEXT_MUTED     = "#6B7280";
const MI6_RED        = "#DC2626";
const GREEN          = "#16A34A";
const ORANGE         = "#EA580C";
const YELLOW         = "#EAB308";
const PROGRESS_TRACK = "#F3F4F6";

/* ─────────────────────────────────────────────────────────────────────────── */
/* Viewport cap — W66.Q (redo of W66.O which was lost in rebase)               */
/* ─────────────────────────────────────────────────────────────────────────── */

const RAIL_MAX_HEIGHT = "calc(100vh - 220px)";

// Silence unused-variable warnings — these tokens are part of the locked
// platform palette and are retained for future use within this file.
void GREEN; void ORANGE; void YELLOW;

/* ─────────────────────────────────────────────────────────────────────────── */
/* Checkpoint metadata                                                         */
/* ─────────────────────────────────────────────────────────────────────────── */

type Checkpoint = {
  num: number;
  name: string;
  floorLabel: string;
};

const CHECKPOINTS: Checkpoint[] = [
  { num: 1,  name: "Project",                    floorLabel: "Level 1 · Reception" },
  { num: 2,  name: "Need / Pain / Demonstration", floorLabel: "Level 2" },
  { num: 3,  name: "Budget",                     floorLabel: "Level 3" },
  { num: 4,  name: "Timeline",                   floorLabel: "Level 4" },
  { num: 5,  name: "Authority — Project / Initiative",        floorLabel: "Level 5" },
  { num: 6,  name: "Technical and Financial Sign-off",          floorLabel: "Level 6" },
  { num: 7,  name: "Org Structure and Influence Map",   floorLabel: "Level 7" },
  { num: 8,  name: "Competitive Position",        floorLabel: "Level 8" },
  { num: 9,  name: "Compliance — IT Internal and Industry-Specific", floorLabel: "Level 9" },
  { num: 10, name: "Legal — Contracts, SLAs, Terms",                 floorLabel: "Level 10 · Top" },
];

/* ─────────────────────────────────────────────────────────────────────────── */
/* Mock deals                                                                  */
/* ─────────────────────────────────────────────────────────────────────────── */

type HealthZone = "think" | "transition" | "know";

// TICKET-3: CPs skipped for fastlane (existing-customer) path.
const FASTLANE_SKIPPED_FLOORS = new Set([3, 4, 7, 8]);

/** Per-CP health, sourced from account_plans.cp_score_breakdown (CANON §13.5/§13.7). */
type CpHealth = { pct: number | null; status: string | null };

type Deal = {
  id: string;
  account: string;
  currentCheckpoint: number; // 1..10. If currentCheckpoint > 10 the deal is fully won.
  /** Highest contiguous CLOSED (green) checkpoint, 0..10. 0 = no CP closed (ground/reception). */
  orbFloor: number;
  /** Per-CP health keyed by cp_number (1..10). Drives per-orb color. */
  cpHealth?: Record<number, CpHealth>;
  daysInCheckpoint: number;
  nextAction: string;
  /** Helix health */
  helixPassed: number;     // 0..10 helix checkpoints passed
  helixTotal: number;      // 10 (CP1–CP10)
  helixFlags: number;      // unresolved disqualifying flags
  helixZone: HealthZone;   // think / transition / know
  /** Attilius recommendation, deal-specific */
  attiliusRecommendation: string;
  /** TICKET-3: Gate 0 routing */
  customerPath?: "full" | "fastlane" | null;
  g0Answer?: "yes" | "no" | null;
};

// Demo-only deals — preserved for Mi6 Demo tenant (is_demo=true). Never shown to production tenants.
const DEMO_DEALS: Deal[] = [
  {
    id: "stellantis",
    account: "Stellantis",
    currentCheckpoint: 3,
    orbFloor: 2, // CP1-CP2 closed, CP3 in-progress
    cpHealth: {
      1: { pct: 88, status: "green" }, 2: { pct: 82, status: "green" },
      3: { pct: 55, status: "orange" },
    },
    daysInCheckpoint: 6,
    nextAction:
      "Schedule a working session with the plant operations lead to confirm problem depth.",
    helixPassed: 3,
    helixTotal: 10,
    helixFlags: 1,
    helixZone: "think",
    attiliusRecommendation:
      "Helix at Level 3 is degrading. 1 yellow flag. Recommend climb to Level 4 with caution — surface the firm or formal timeline before next call.",
  },
  {
    id: "toyota",
    account: "Toyota",
    currentCheckpoint: 7,
    orbFloor: 6, // CP1-CP6 closed, CP7 in-progress
    cpHealth: {
      1: { pct: 90, status: "green" }, 2: { pct: 85, status: "green" },
      3: { pct: 81, status: "green" }, 4: { pct: 84, status: "green" },
      5: { pct: 80, status: "green" }, 6: { pct: 83, status: "green" },
      7: { pct: 68, status: "yellow" },
    },
    daysInCheckpoint: 11,
    nextAction:
      "Push for written budget confirmation from Procurement before EOM forecast lock.",
    helixPassed: 6,
    helixTotal: 10,
    helixFlags: 0,
    helixZone: "transition",
    attiliusRecommendation:
      "Helix is healthy. 6 of 10 checkpoints passed. Continue climbing — Level 8 (Business Case) next.",
  },
  {
    id: "gm",
    account: "GM",
    currentCheckpoint: 1,
    orbFloor: 0, // no CP closed yet — orb rests at ground / reception
    cpHealth: {
      1: { pct: 35, status: "red" },
    },
    daysInCheckpoint: 2,
    nextAction:
      "Complete account research pack — top three pains, recent earnings call signals.",
    helixPassed: 0,
    helixTotal: 10,
    helixFlags: 0,
    helixZone: "think",
    attiliusRecommendation:
      "Early Helix signal. Complete account research pack before climbing past Level 2.",
  },
  {
    id: "ford",
    account: "Ford",
    currentCheckpoint: 10,
    orbFloor: 10, // all CPs closed — closed won
    cpHealth: {
      1: { pct: 92, status: "green" }, 2: { pct: 90, status: "green" },
      3: { pct: 88, status: "green" }, 4: { pct: 86, status: "green" },
      5: { pct: 85, status: "green" }, 6: { pct: 84, status: "green" },
      7: { pct: 87, status: "green" }, 8: { pct: 83, status: "green" },
      9: { pct: 89, status: "green" }, 10: { pct: 91, status: "green" },
    },
    daysInCheckpoint: 4,
    nextAction:
      "Secure countersigned MSA. Hand off to onboarding once written commitment lands.",
    helixPassed: 10,
    helixTotal: 10,
    helixFlags: 0,
    helixZone: "know",
    attiliusRecommendation: "Closed won. No further action.",
  },
];

/* ─────────────────────────────────────────────────────────────────────────── */
/* Canon tower image + deal-position orb overlay                               */
/* ─────────────────────────────────────────────────────────────────────────── */

/**
 * Floor y-positions as percentage of image height (top=0%, bottom=100%).
 * W66.X1 — Re-anchored to actual illuminated rings on
 * /hero/mi6-tower-clean-desktop.webp via pixel-brightness peak detection.
 * 10 lit rings detected from y=15.4% (top crown ring) down to y=57.4%
 * (lowest ring above the reception lobby). Each orb sits ON a ring.
 * Bryan canon: CP10 = top ring (highest floor), CP1 = bottom ring (lowest
 * floor above waterline). Range matches the 10-floor illuminated mapping.
 */
const FLOOR_Y_PCT: Record<number, number> = {
  1: 58.76, 2: 53.71, 3: 48.71, 4: 43.71, 5: 38.71,
  6: 33.77, 7: 28.83, 8: 23.89, 9: 18.95, 10: 14.06,
};

/**
 * Returns the canonical 5-color orb hex for a given composite_score band.
 * Matches the LOCKED CANON forecast bands:
 *   >=80 Commit (green) · 65-79 Upside (yellow) · 50-64 Best Case (orange) ·
 *   30-49 Pipeline (red) · <30 or null/0 Omit (gray).
 */
function orbColor(score: number | null | undefined): string {
  if (score === null || score === undefined || score === 0) return "#6B7280"; // gray — Omit / not scored
  if (score < 30)  return "#6B7280"; // gray — Omit
  if (score < 50)  return "#DC2626"; // red — Pipeline
  if (score < 65)  return "#EA580C"; // orange — Best Case
  if (score < 80)  return "#EAB308"; // yellow — Upside
  return "#16A34A";                   // green — Commit
}

/**
 * Maps a per-CP closure status band to its canonical 5-color hex.
 * Status bands come from account_cp_status / cp_score_breakdown and are already
 * floor-enforced server-side (CANON §13.5 sequential close): a CP can only be
 * 'green' when it AND every prior CP are fully closed. null/unknown -> gray.
 */
function statusColor(status: string | null | undefined): string {
  switch (status) {
    case "green":  return "#16A34A";
    case "yellow": return "#EAB308";
    case "orange": return "#EA580C";
    case "red":    return "#DC2626";
    default:       return "#6B7280"; // null / no_data — Omit / not scored
  }
}

/**
 * Resolves a single checkpoint's orb color from its per-CP health.
 * Prefers the numeric pct through the CANON forecast bands (orbColor, the SOLE
 * score-to-color function) so per-orb color stays identical to the global band
 * logic. Falls back to the server-computed status band when pct is absent.
 */
function cpOrbColor(health: CpHealth | undefined): string {
  if (!health) return "#6B7280";
  if (health.pct !== null && health.pct !== undefined) return orbColor(health.pct);
  return statusColor(health.status);
}

/**
 * W3-Tower canon: Clean tower (no helix inside) with 10 colored orbs.
 * Bryan's rule: tower WITHOUT helix in middle. 3D helix is a SEPARATE visual to the right.
 *
 * TOWER METAPHOR CONTRACT (hotfix 2026-06-11 — F):
 *   FLOOR  = highest contiguous CLOSED checkpoint (orbFloor, 0..10). A CP is
 *            "closed" only when its server status is 'green' AND every prior CP
 *            is closed (CANON §13.5 sequential close, §13.7 orb position).
 *            orbFloor=0 means CP1 is not closed -> the deal sits at ground /
 *            reception, NOT floor 1. Partial answers never lift the floor.
 *   COLOR  = health of EACH checkpoint's own orb, taken from that CP's per-CP
 *            health (cpHealth) through the CANON forecast bands — never a single
 *            global composite. The current-floor (or next in-progress) orb pulses.
 *
 * W66.Q Fix 12/13: the pulsing orb also tracks `selectedCheckpoint` (user-tapped
 * CP) so taps in the Helix panel move the highlight; color stays per-CP.
 */
function CanonTowerImage({
  currentCheckpoint,
  selectedCheckpoint,
  orbFloor,
  cpHealth,
}: {
  currentCheckpoint: number;
  selectedCheckpoint: number;
  orbFloor: number;
  cpHealth?: Record<number, CpHealth>;
  variant?: "clean" | "helix-inside";
}) {
  // The in-progress CP is the one just above the highest closed floor.
  // orbFloor 0 (nothing closed) -> in-progress CP1, orb anchored at the
  // CP1 ring but flagged as not-yet-closed.
  const floor       = Math.max(0, Math.min(10, orbFloor));
  const inProgressCp = Math.min(10, floor + 1);
  const selectedCp  = Math.max(1, Math.min(10, selectedCheckpoint));
  void currentCheckpoint; // retained for API symmetry; floor now drives position

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        maxWidth: 400,
        margin: "0 auto",
      }}
    >
      {/* W3-Tower: clean tower — no helix inside */}
      <picture>
        <source media="(max-width: 768px)" srcSet="/hero/mi6-tower-clean-mobile.webp" />
        <source media="(min-width: 769px)" srcSet="/hero/mi6-tower-clean-desktop.webp" />
        <img
          src="/hero/mi6-tower-clean-desktop.webp"
          alt="Mi6 Tower — process snapshot"
          style={{ width: "100%", display: "block", borderRadius: 8, objectFit: "contain", objectPosition: "center top" }}
        />
      </picture>

      {/* 10 colored orbs — one per checkpoint, colored by THAT checkpoint's own
           health (per CANON bands). Closed CPs (<= floor) carry their real color;
           the in-progress CP shows its partial color; CPs beyond it are gray
           (not reached). The position highlight pulses on the floor / in-progress
           CP (or the user-selected CP when tapped in the Helix). */}
      {Array.from({ length: 10 }, (_, i) => {
        const cpNum = i + 1;
        const yPct = FLOOR_Y_PCT[cpNum] ?? 50;
        const isClosed     = cpNum <= floor;
        const isInProgress = cpNum === inProgressCp && floor < 10;
        const isPending    = cpNum > inProgressCp;
        const isCleared    = isClosed;
        const isCurrent    = isInProgress;
        const isSelected   = cpNum === selectedCp;
        // Per-CP color: closed and in-progress CPs use their own health band;
        // CPs not yet reached are gray. Health may be absent -> cpOrbColor -> gray.
        const color = isPending && !isSelected
          ? "#6B7280"
          : cpOrbColor(cpHealth?.[cpNum]);
        return (
          <div
            key={cpNum}
            aria-label={`Checkpoint ${cpNum}${
              isSelected ? " — viewing" : ""
            }${isCurrent ? " — current" : isCleared ? " — cleared" : " — pending"}`}
            style={{
              position: "absolute",
              left: "50%",
              top: `${yPct}%`,
              transform: "translate(-50%, -50%)",
              width: isSelected ? 16 : 11,
              height: isSelected ? 16 : 11,
              borderRadius: "50%",
              background: color,
              boxShadow: isSelected
                ? `0 0 10px 4px ${color}99, 0 0 4px 1px ${color}`
                : `0 0 6px 2px ${color}66`,
              border: `${isSelected ? 2 : 1.5}px solid rgba(255,255,255,${
                isSelected ? 0.8 : isCleared ? 0.6 : 0.3
              })`,
              zIndex: 10,
              opacity: isPending && !isSelected ? 0.35 : 1,
              animation: isSelected ? "orbPulse 1.8s ease-in-out infinite" : "none",
            }}
          />
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Helix SVG — single-deal health visualization                                */
/* ─────────────────────────────────────────────────────────────────────────── */

/**
 * Renders a vertical DNA double-helix for ONE deal.
 *
 *   - Two strands sine-wave around a central spine.
 *   - 9 rungs / gates evenly spaced bottom→top.
 *   - Bottom = "I think" zone (charcoal tones).
 *   - Top    = "I know"  zone (red tones).
 *   - Passed gates  → solid red, glowing.
 *   - Open gates    → charcoal outline.
 *   - Flagged gates → red ring on charcoal core (yellow not allowed by palette;
 *     we use a pulsing red ring instead).
 *
 * Total height matches the Tower SVG (~720) for visual symmetry.
 *
 * NOTE: HelixSvg is an internal data-visualization SVG. Its dark strand and gate
 * colors (#1F1F23, #DC2626) are locked helix-canon and are NOT part of the UI
 * chrome light-theme migration.
 */
function HelixSvg({
  passed,
  total,
  flags,
  zone,
}: {
  passed: number;
  total: number;
  flags: number;
  zone: HealthZone;
}) {
  const W = 240;
  const H = 720;
  const cx = W / 2;
  const top = 60;
  const bot = H - 60;
  const amp = 56;          // horizontal swing of each strand
  const cycles = 3.4;      // number of sine cycles top→bottom

  // Build a smooth strand path with a phase offset.
  const buildStrand = (phase: number, steps = 160) => {
    const pts: string[] = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const y = top + t * (bot - top);
      const x = cx + amp * Math.sin(t * Math.PI * cycles + phase);
      pts.push(`${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`);
    }
    return pts.join(" ");
  };

  const strandFront = buildStrand(0);
  const strandBack  = buildStrand(Math.PI);

  // Gate positions — 9 rungs, rendered top → bottom along the SVG.
  // Gate numbers run from BOTTOM (G1) to TOP (G9). As you confirm gates
  // you climb the helix from "I think" up toward "I know".
  // i=0 is the top-most rung in SVG terms.
  const gates = Array.from({ length: total }, (_, i) => {
    const t = (i + 0.5) / total;            // 0..1 from top in SVG space
    const y = top + t * (bot - top);
    const xFront = cx + amp * Math.sin(t * Math.PI * cycles);
    const xBack  = cx + amp * Math.sin(t * Math.PI * cycles + Math.PI);
    // Gate number from the bottom (G1 = bottom, Gtotal = top).
    const gateNumber = total - i;
    return { i, t, y, xFront, xBack, gateNumber };
  });

  // Passed gates fill from the BOTTOM up. The bottom-most `passed` gates
  // (G1..G{passed}) are the ones already cleared. The next gate to clear is
  // G{passed+1}, which sits one row higher.
  const nextGateNumber = passed + 1;        // next gate to clear (1-indexed from bottom)
  // For the "flag" treatment, attach it to the next un-passed gate when
  // flags > 0. This keeps everything inside palette.
  const flaggedGate = flags > 0 ? nextGateNumber : -1;
  void flaggedGate; // silence unused warning when flags === 0

  // Zone label
  const zoneLabel =
    zone === "know"
      ? "I know"
      : zone === "transition"
      ? "I think → I know"
      : "I think";

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height="100%"
      style={{ maxHeight: 720, maxWidth: 240 }}
      role="img"
      aria-label="Helix deal health visualization"
    >
      <defs>
        <filter id="helixRedGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Vertical gradient: charcoal at bottom (think) → red at top (know).
            Locked palette: only #1F1F23 and #DC2626. */}
        <linearGradient id="helixSpineGrad" x1="0" x2="0" y1="1" y2="0">
          <stop offset="0%"   stopColor="#1F1F23" stopOpacity="0.85" />
          <stop offset="55%"  stopColor="#DC2626" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#DC2626" stopOpacity="0.95" />
        </linearGradient>

        {/* Strand stroke gradient — same think→know shift */}
        <linearGradient id="helixStrandGrad" x1="0" x2="0" y1="1" y2="0">
          <stop offset="0%"   stopColor="#1F1F23" stopOpacity="0.9" />
          <stop offset="50%"  stopColor="#DC2626" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#DC2626" stopOpacity="0.95" />
        </linearGradient>
      </defs>

      {/* Soft vertical spine — visual centerline */}
      <line
        x1={cx}
        x2={cx}
        y1={top - 12}
        y2={bot + 12}
        stroke="url(#helixSpineGrad)"
        strokeWidth={1}
        opacity={0.35}
      />

      {/* Cap markers — top = "know" / bottom = "think" */}
      <circle cx={cx} cy={top - 14} r={3} fill="#DC2626" filter="url(#helixRedGlow)" />
      <circle cx={cx} cy={bot + 14} r={3} fill="#1F1F23" stroke="#FAFAF7" strokeOpacity={0.35} strokeWidth={0.6} />

      {/* Back strand */}
      <path
        d={strandBack}
        fill="none"
        stroke="url(#helixStrandGrad)"
        strokeWidth={2}
        opacity={0.45}
      />

      {/* Cross-rungs — one per gate, behind orbs */}
      {gates.map((g) => {
        const isPassed = g.gateNumber <= passed;
        return (
          <line
            key={`rung-${g.i}`}
            x1={g.xFront}
            x2={g.xBack}
            y1={g.y}
            y2={g.y}
            stroke={isPassed ? "#DC2626" : "#1F1F23"}
            strokeWidth={isPassed ? 1.2 : 0.9}
            opacity={isPassed ? 0.75 : 0.55}
            strokeDasharray={isPassed ? undefined : "3 3"}
          />
        );
      })}

      {/* Front strand */}
      <path
        d={strandFront}
        fill="none"
        stroke="url(#helixStrandGrad)"
        strokeWidth={2.4}
        opacity={0.95}
      />

      {/* Gate orbs */}
      {gates.map((g) => {
        const isPassed = g.gateNumber <= passed;
        const isFlagged = g.gateNumber === flaggedGate;
        const isCurrent =
          !isPassed && g.gateNumber === nextGateNumber && passed < total;

        // Tier 1 = first 5 gates (largest)
        const r = g.gateNumber <= 5 ? 9 : 7;

        const fill = isPassed ? "#DC2626" : "#1F1F23";
        const stroke = isPassed
          ? "#DC2626"
          : isFlagged
          ? "#DC2626"
          : "#FAFAF7";
        const strokeOpacity = isPassed ? 1 : isFlagged ? 0.95 : 0.18;

        return (
          <g key={`orb-${g.i}`}>
            {/* Outer halo for passed/current */}
            {(isPassed || isCurrent) && (
              <circle
                cx={g.xFront}
                cy={g.y}
                r={r + 6}
                fill="rgba(220,38,38,0.18)"
              />
            )}

            {/* Flag ring — pulsing red ring on charcoal core */}
            {isFlagged && !isPassed && (
              <circle
                cx={g.xFront}
                cy={g.y}
                r={r + 4}
                fill="none"
                stroke="#DC2626"
                strokeWidth={1.3}
                opacity={0.9}
                className="helix-flag-pulse"
              />
            )}

            {/* Main orb */}
            <circle
              cx={g.xFront}
              cy={g.y}
              r={r}
              fill={fill}
              stroke={stroke}
              strokeOpacity={strokeOpacity}
              strokeWidth={1.2}
              filter={isPassed ? "url(#helixRedGlow)" : undefined}
              className={isCurrent ? "helix-current-pulse" : ""}
            />

            {/* Subtle shine on passed orbs */}
            {isPassed && (
              <circle
                cx={g.xFront - r * 0.3}
                cy={g.y - r * 0.3}
                r={r * 0.28}
                fill="rgba(250,250,247,0.55)"
                pointerEvents="none"
              />
            )}

            {/* Gate number label, side of spine */}
            <text
              x={g.xFront + (g.xFront > cx ? r + 6 : -(r + 6))}
              y={g.y + 3.5}
              fontSize={9}
              fill={
                isPassed
                  ? "#FAFAF7"
                  : isFlagged
                  ? "#DC2626"
                  : "rgba(250,250,247,0.55)"
              }
              textAnchor={g.xFront > cx ? "start" : "end"}
              fontFamily="Inter, sans-serif"
              fontWeight={isPassed || isFlagged ? 700 : 500}
            >
              {`G${g.gateNumber}`}
            </text>
          </g>
        );
      })}

      {/* Zone labels — top "know", bottom "think" */}
      <text
        x={cx}
        y={top - 26}
        fontSize={10}
        fill="#DC2626"
        textAnchor="middle"
        fontFamily="'DM Sans', Inter, sans-serif"
        fontWeight={700}
        letterSpacing={1.2}
      >
        I&nbsp;KNOW
      </text>
      <text
        x={cx}
        y={bot + 30}
        fontSize={10}
        fill="rgba(250,250,247,0.55)"
        textAnchor="middle"
        fontFamily="'DM Sans', Inter, sans-serif"
        fontWeight={700}
        letterSpacing={1.2}
      >
        I&nbsp;THINK
      </text>

      {/* Center zone callout */}
      <text
        x={cx}
        y={H - 8}
        fontSize={9.5}
        fill="rgba(250,250,247,0.7)"
        textAnchor="middle"
        fontFamily="Inter, sans-serif"
        fontWeight={600}
      >
        {zoneLabel}
      </text>
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Page                                                                        */
/* ─────────────────────────────────────────────────────────────────────────── */

/** Per-account cp_score_breakdown from account_plans, keyed by account_id. */
type BreakdownByAccount = Map<number, Record<string, { pct: number | null; status: string | null }>>;

/**
 * TOWER METAPHOR (hotfix 2026-06-11 — F). Builds the deal list that drives the
 * Tower from the live score tables. Two truths must hold:
 *
 *   FLOOR (orbFloor) — CANON §13.7: highest contiguous CLOSED checkpoint, i.e.
 *   the highest n where CP1..CPn are ALL 'green'. getOrbCpFromRows already
 *   enforces this contiguous-closure rule against account_cp_status.status
 *   (which is itself sequential-floor-enforced server-side, CANON §13.5). We
 *   keep the raw 0..10 value: 0 means CP1 is not closed and the orb must rest
 *   at the ground / reception level, NOT be lifted to floor 1. Entering CP3
 *   answers without closing CP1 leaves orbFloor at 0.
 *
 *   COLOR (cpHealth) — each checkpoint orb is colored by ITS OWN health, not a
 *   global composite. We prefer account_plans.cp_score_breakdown (per-CP pct +
 *   status, already floor-enforced) and fall back to the account_cp_status band
 *   when no breakdown row exists.
 */
function mapCpDataToDeals(data: any[], breakdownByAccount: BreakdownByAccount): Deal[] {
  // Group all CP rows by account_id
  const byAccount = new Map<number, { name: string; cpRows: Array<{ cp_number: number; status: string | null }> }>();
  for (const row of data) {
    const aid = row.account_id as number;
    if (!byAccount.has(aid)) {
      byAccount.set(aid, {
        name: row.accounts?.name ?? `Account ${aid}`,
        cpRows: [],
      });
    }
    byAccount.get(aid)!.cpRows.push({ cp_number: row.cp_number, status: row.status ?? null });
  }

  const deals: Deal[] = [];
  for (const [accountId, { name, cpRows }] of byAccount.entries()) {
    // CANON §13.7: orb floor = highest contiguous green (closed) run from CP1, 0..10.
    const orbFloor = getOrbCpFromRows(cpRows);

    // Per-CP health: prefer the cp_score_breakdown row; fall back to the
    // account_cp_status band so an orb still colors when breakdown is absent.
    const breakdown = breakdownByAccount.get(accountId);
    const statusByCp = new Map<number, string | null>();
    for (const r of cpRows) statusByCp.set(r.cp_number, r.status ?? null);

    const cpHealth: Record<number, CpHealth> = {};
    for (let n = 1; n <= 10; n++) {
      const b = breakdown?.[String(n)];
      if (b) {
        cpHealth[n] = { pct: b.pct ?? null, status: b.status ?? null };
      } else {
        cpHealth[n] = { pct: null, status: statusByCp.get(n) ?? null };
      }
    }

    deals.push({
      id: String(accountId),
      account: name,
      // The rail still labels the in-progress CP as "current"; floor+1 is the
      // checkpoint being worked. orbFloor is the source of truth for position.
      currentCheckpoint: Math.min(10, orbFloor + 1),
      orbFloor,
      cpHealth,
      daysInCheckpoint: 0,
      nextAction: "Open account to review next action.",
      helixPassed: 0,
      helixTotal: 10,
      helixFlags: 0,
      helixZone: "think" as HealthZone,
      attiliusRecommendation: "Score this account through M-PACT to generate Attilius recommendations.",
      customerPath: null,
      g0Answer: null,
    });
  }
  return deals;
}

export default function Tower() {
  const session = getSession();
  const tenant = getCurrentTenant();
  const isDemo = tenant?.is_demo === true;
  const tenantId = session?.tenantId ?? 1;

  const [liveDeals, setLiveDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);

  // W66.Q Fix 12/13: selectedCp tracks which CP the user has tapped to view.
  // Defaults to currentCheckpoint on load; resets when the active deal changes.
  // Distinct from currentCheckpoint (deal position) which is immutable here.
  const [selectedCp, setSelectedCp] = useState<number>(1);

  useEffect(() => {
    if (isDemo) {
      setLiveDeals(DEMO_DEALS);
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const sb = supabase();
        const { data } = await sb
          .from("account_cp_status")
          // TICKET-3: include customer_path + g0_answer for fastlane floor filtering
          // FENCE-FIX: customer_path and g0_answer are stored in aip_ext JSONB,
          // not as separate columns on accounts. Removing them from the select
          // prevents the PostgREST 400 (column does not exist).
          .select("account_id, cp_number, status, accounts!inner(name, tenant_id)")
          .eq("accounts.tenant_id", tenantId);

        if (cancelled) return;

        if (!data || data.length === 0) {
          setLiveDeals([]);
          setLoading(false);
          return;
        }

        // Per-CP health for orb color: account_plans.cp_score_breakdown holds
        // the floor-enforced per-CP pct + status (CANON §13.5/§13.7). account_id
        // is stored as text on account_plans, so match against string ids.
        const accountIds = Array.from(
          new Set((data as any[]).map((r) => String(r.account_id)))
        );
        const breakdownByAccount: BreakdownByAccount = new Map();
        const { data: planData } = await sb
          .from("account_plans")
          .select("account_id, cp_score_breakdown")
          .in("account_id", accountIds);
        for (const row of (planData ?? []) as any[]) {
          const aid = Number(row.account_id);
          if (!Number.isNaN(aid) && row.cp_score_breakdown) {
            breakdownByAccount.set(aid, row.cp_score_breakdown);
          }
        }

        if (cancelled) return;
        setLiveDeals(mapCpDataToDeals(data, breakdownByAccount));
        setLoading(false);
      } catch {
        if (cancelled) return;
        setLiveDeals([]);
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isDemo, tenantId]);

  // P0-1: In preview mode (no real data), surface DEMO_DEALS in the selector
  const deals = liveDeals.length > 0 ? liveDeals : DEMO_DEALS;
  const defaultDealId = deals.length > 0 ? deals[0].id : "";
  const [dealId, setDealId] = useState<string>(defaultDealId);

  // Keep dealId in sync if deals change after load
  const activeDealId = deals.find((d) => d.id === dealId) ? dealId : (deals[0]?.id ?? "");

  const deal = useMemo(
    () => deals.find((d) => d.id === activeDealId) ?? deals[0],
    [activeDealId, deals]
  );

  // Reset selectedCp to deal's currentCheckpoint whenever the active deal changes.
  useEffect(() => {
    if (deal) setSelectedCp(deal.currentCheckpoint);
  }, [deal?.id]);

  // Loading state
  if (loading) {
    return (
      <OperatorShell breadcrumbs={[{ label: "Tower" }]} pageTitle="Tower" pageMeta="Tower">
        <div className="card-shell p-8 text-center">
          <p className="text-sm text-muted-foreground">Loading&hellip;</p>
        </div>
      </OperatorShell>
    );
  }

  // P0-1: Preview mode flag — shown only when no real account data exists
  const isPreviewMode = !isDemo && liveDeals.length === 0;

  // Empty state guard (should not reach here since deals always has DEMO_DEALS fallback)
  if (!deal) {
    return (
      <OperatorShell breadcrumbs={[{ label: "Tower" }]} pageTitle="Tower" pageMeta="Tower">
        <div className="card-shell p-8 text-center">
          <p className="text-sm text-muted-foreground">
            The Tower fills as your team scores accounts through the M-PACT checkpoints.
          </p>
        </div>
      </OperatorShell>
    );
  }

  // Cleared = closed checkpoints only (orbFloor), NOT the in-progress one.
  const cleared = Math.min(deal.orbFloor, CHECKPOINTS.length);
  const currentCp = CHECKPOINTS[Math.min(deal.currentCheckpoint, CHECKPOINTS.length) - 1];

  return (
    <OperatorShell breadcrumbs={[{ label: "Tower" }]} pageTitle="Tower" pageMeta="Tower">
      {/* P0-1: Preview mode banner shown when no real account_cp_status data exists */}
      {isPreviewMode && (
        <div
          style={{
            marginBottom: 12,
            padding: "8px 16px",
            background: "rgba(220,38,38,0.08)",
            border: "1px solid rgba(220,38,38,0.22)",
            borderRadius: 8,
            fontSize: 12,
            color: MI6_RED,
            fontFamily: "'Inter', sans-serif",
            fontWeight: 500,
            letterSpacing: "0.01em",
          }}
        >
          Preview mode — sample data. Score accounts through M-PACT checkpoints to see your team's live tower.
        </div>
      )}
      {/* Inline keyframes for the pulsing current floor and helix flag.
          Scoped class names avoid collisions with other animations. */}
      <style>{`
        @keyframes towerFloorPulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.55; }
        }
        .tower-floor-pulse {
          animation: towerFloorPulse 1.6s ease-in-out infinite;
          transform-origin: center;
        }
        @keyframes helixFlagPulse {
          0%, 100% { opacity: 0.95; }
          50%      { opacity: 0.35; }
        }
        .helix-flag-pulse {
          animation: helixFlagPulse 1.4s ease-in-out infinite;
        }
        @keyframes helixCurrentPulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.55; }
        }
        .helix-current-pulse {
          animation: helixCurrentPulse 1.6s ease-in-out infinite;
        }
        @keyframes orbPulse {
          0%, 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          50%      { opacity: 0.75; transform: translate(-50%, -50%) scale(1.18); }
        }
      `}</style>

      <div className="space-y-6">
        {/* ── Deal selector ─────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label
              htmlFor="tower-deal-select"
              className="block text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5"
            >
              Deal
            </label>
            <select
              id="tower-deal-select"
              value={dealId}
              onChange={(e) => setDealId(e.target.value)}
              className="rounded-md border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#DC2626]/40 min-w-[220px]"
              style={{ background: CARD, color: TEXT_PRIMARY }}
            >
              {deals.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.account} — CP{d.currentCheckpoint}
                </option>
              ))}
            </select>
          </div>
          <p className="text-[12px] text-muted-foreground max-w-md leading-relaxed">
            One deal, two lenses. The Tower shows where it sits on the approval
            ladder. The Helix shows how confident we are it&rsquo;s real.
          </p>
        </div>

        {/* ── Header / status panel ─────────────────────────────────────── */}
        <div
          className="rounded-xl border overflow-hidden"
          style={{ background: CARD, borderColor: BORDER }}
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
            {/* Account block */}
            <div
              className="md:col-span-4 py-6 px-6 border-b md:border-b-0 md:border-r"
              style={{ borderColor: BORDER }}
            >
              <div
                className="text-[11px] uppercase tracking-[0.14em] mb-1"
                style={{ color: TEXT_MUTED }}
              >
                Account
              </div>
              <h2
                className="text-3xl leading-tight tracking-tight"
                style={{ fontFamily: "'DM Sans', Inter, system-ui", color: TEXT_PRIMARY }}
              >
                {deal.account}
              </h2>
              <div className="mt-2 text-[12px]" style={{ color: TEXT_SECONDARY }}>
                CP{currentCp.num} — {currentCp.name}
              </div>
            </div>

            {/* Cleared block */}
            <div
              className="md:col-span-4 py-6 px-6 border-b md:border-b-0 md:border-r"
              style={{ borderColor: BORDER }}
            >
              <div
                className="text-[11px] uppercase tracking-[0.14em] mb-1"
                style={{ color: TEXT_MUTED }}
              >
                Cleared
              </div>
              <div
                className="text-2xl"
                style={{ fontFamily: "'DM Sans', Inter, system-ui", color: TEXT_PRIMARY }}
              >
                {cleared} <span className="text-base" style={{ color: TEXT_MUTED }}>of {CHECKPOINTS.length}</span>
              </div>
              <div className="mt-2 h-1.5 w-full rounded-full overflow-hidden" style={{ background: PROGRESS_TRACK }}>
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${(cleared / CHECKPOINTS.length) * 100}%`,
                    background: MI6_RED,
                    boxShadow: "0 0 8px rgba(220,38,38,0.55)",
                    transition: "width 400ms ease-out",
                  }}
                />
              </div>
              <div className="mt-2 text-[12px]" style={{ color: TEXT_MUTED }}>
                {deal.daysInCheckpoint} day{deal.daysInCheckpoint === 1 ? "" : "s"} in current Checkpoint
              </div>
            </div>

            {/* Next action block */}
            <div className="md:col-span-4 py-6 px-6">
              <div
                className="text-[11px] uppercase tracking-[0.14em] mb-1"
                style={{ color: TEXT_MUTED }}
              >
                Next Action
              </div>
              <p className="text-[13px] leading-relaxed" style={{ color: TEXT_SECONDARY }}>
                {deal.nextAction}
              </p>
            </div>
          </div>
        </div>

        {/* ── Attilius recommendation ─────────────────────────── */}
        <div
          className="rounded-xl overflow-hidden"
          style={{
            background: CARD,
            border: `1px solid ${BORDER}`,
            borderLeft: `3px solid ${MI6_RED}`,
          }}
        >
          <div className="py-5 px-6">
            <div
              className="text-[11px] uppercase tracking-[0.14em] mb-1.5"
              style={{ color: MI6_RED }}
            >
              Attilius Recommendation
            </div>
            <p
              className="text-[14px] leading-relaxed"
              style={{
                color: TEXT_PRIMARY,
                fontFamily: "Inter, system-ui",
              }}
            >
              {deal.attiliusRecommendation}
            </p>
          </div>
        </div>

        {/* ── Two-column body: Tower (left) + Helix (right) ─────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Tower panel */}
          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <div className="text-[11px] uppercase tracking-[0.14em]" style={{ color: TEXT_MUTED }}>
                Process — where in the approval ladder
              </div>
              <div className="text-[11px]" style={{ color: TEXT_MUTED }}>
                CP{deal.currentCheckpoint} of 10
              </div>
            </div>
            <div
              className="rounded-xl border overflow-hidden"
              style={{ background: CARD, borderColor: BORDER }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-0">
                {/* LaneT2: Clean tower (no helix) with 10 floor orbs.
                     Semantics: process snapshot — where the deal is in the M-PACT sequence.
                     Photo container background is intentionally BG (#F9FAFB) — the tower
                     photo itself is a hero image and is not touched. */}
                <div
                  className="sm:col-span-5 flex items-center justify-center py-8 px-4"
                  style={{ background: BG }}
                >
                  <CanonTowerImage
                    currentCheckpoint={deal.currentCheckpoint}
                    selectedCheckpoint={selectedCp}
                    orbFloor={deal.orbFloor}
                    cpHealth={deal.cpHealth}
                    variant="clean"
                  />
                </div>

                {/* Checkpoints rail */}
                <div
                  className="sm:col-span-7 py-8 px-5 border-t sm:border-t-0 sm:border-l"
                  style={{ borderColor: BORDER, background: CARD }}
                >
                  {/* TICKET-3: fastlane badge */}
                  {deal.customerPath === "fastlane" && (
                    <div style={{
                      display: "inline-flex", alignItems: "center",
                      background: "rgba(0,0,0,0.04)", border: `1px solid ${BORDER}`,
                      borderRadius: 4, padding: "3px 8px", marginBottom: 10,
                      fontFamily: "'DM Sans', Inter, system-ui", fontSize: 10,
                      fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                      color: TEXT_SECONDARY,
                    }}>
                      Existing Customer — Fastlane Path
                    </div>
                  )}
                  <div
                    className="text-[11px] uppercase tracking-[0.14em] mb-3"
                    style={{ color: TEXT_MUTED }}
                  >
                    Checkpoints
                  </div>
                  <ol
                    className="space-y-1 lg:overflow-y-auto"
                    style={{ maxHeight: RAIL_MAX_HEIGHT }}
                  >
                    {/* Render top-down: CP10 first. TICKET-3: skipped CPs dimmed for fastlane. */}
                    {[...CHECKPOINTS].reverse().map((cp) => {
                      const isFastlaneSkipped = deal.customerPath === "fastlane" && FASTLANE_SKIPPED_FLOORS.has(cp.num);
                      const isCleared = !isFastlaneSkipped && cp.num < deal.currentCheckpoint;
                      const isCurrent = !isFastlaneSkipped && cp.num === deal.currentCheckpoint;
                      return (
                        <li
                          key={cp.num}
                          className="flex items-center gap-2.5 py-1"
                          style={{ opacity: isFastlaneSkipped ? 0.35 : 1 }}
                        >
                          <span
                            aria-hidden
                            style={{
                              display: "inline-block",
                              width: 9,
                              height: 9,
                              borderRadius: 999,
                              background:
                                isCleared || isCurrent ? MI6_RED : "transparent",
                              border:
                                isCleared || isCurrent
                                  ? `1px solid ${MI6_RED}`
                                  : `1px solid ${BORDER}`,
                              boxShadow:
                                isCleared || isCurrent
                                  ? "0 0 8px rgba(220,38,38,0.55)"
                                  : "none",
                            }}
                            className={isCurrent ? "tower-floor-pulse" : ""}
                          />
                          <div className="flex-1 min-w-0">
                            <div
                              className="text-[12.5px]"
                              style={{
                                color:
                                  isCleared || isCurrent
                                    ? TEXT_PRIMARY
                                    : TEXT_MUTED,
                                fontWeight: isCurrent ? 600 : 500,
                              }}
                            >
                              <span style={{ fontFamily: "'DM Sans', Inter, system-ui" }}>
                                Level {cp.num}
                              </span>
                              <span className="mx-1.5" style={{ color: BORDER }}>—</span>
                              <span style={{ fontFamily: "'DM Sans', Inter, system-ui" }}>
                                CP{cp.num}
                              </span>
                              <span className="mx-1.5" style={{ color: BORDER }}>—</span>
                              {cp.name}
                            </div>
                          </div>
                          {isCleared && (
                            <span className="text-[9.5px] uppercase tracking-[0.14em]" style={{ color: MI6_RED }}>
                              Cleared
                            </span>
                          )}
                          {isCurrent && (
                            <span className="text-[9.5px] uppercase tracking-[0.14em]" style={{ color: MI6_RED }}>
                              Current
                            </span>
                          )}
                          {isFastlaneSkipped && (
                            <span className="text-[9.5px] uppercase tracking-[0.14em]" style={{ color: TEXT_MUTED }}>
                              Skipped: existing customer
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {/* LaneT2: Helix panel — 3D OperationalHelix as side-by-side visual
               Semantics: health of the deal (answer-state per CP, color/health distribution).
               View-only (readOnly=true). Orb-tap-to-read drawer + zoom already wired in component. */}
          <div className="space-y-2">
            <div className="flex items-baseline justify-between">
              <div className="text-[11px] uppercase tracking-[0.14em]" style={{ color: TEXT_MUTED }}>
                Deal DNA — health of the deal
              </div>
              <div className="text-[11px]" style={{ color: TEXT_MUTED }}>
                {deal.helixPassed} of {deal.helixTotal} Checkpoints
              </div>
            </div>
            <div
              className="rounded-xl border overflow-hidden lg:overflow-y-auto"
              style={{ background: CARD, borderColor: BORDER, minHeight: 480, maxHeight: RAIL_MAX_HEIGHT }}
            >
              {/* W44.2: readOnly=true — Tower is view-only. hideHeader=true suppresses
                   the "Opportunity DNA" title so the panel reads as "Deal DNA" (Tower context).
                   W66.Q Fix 12: focusCp + onCpSelect keep Tower selectedCp in sync with
                   the CP chip the user taps inside the Helix panel. */}
              <OperationalHelix
                accountId={deal.id}
                accountName={deal.account}
                readOnly={true}
                hideHeader={true}
                focusCp={selectedCp}
                onCpSelect={(cp) => { if (cp !== null) setSelectedCp(cp); }}
              />
            </div>
          </div>
        </div>

        {/* ── Footnote ─────────────────────────────────────────────────── */}
        <p className="text-[11px] text-muted-foreground leading-relaxed max-w-2xl">
          Process tracks Level. Health tracks confidence. Fire escapes track discipline.
        </p>
        {/* T2 Item 4 — Color Legend Footer (Canon §19) */}
        <ColorLegendFooter />
      </div>
    </OperatorShell>
  );
}
